<template>
  <div
    v-if="show"
    ref="backdrop"
    class="ds-dialog"
    role="dialog"
    aria-modal="true"
    :aria-labelledby="`${id}-modal-title`"
    :data-qa="`${id}-modal`"
    @mousedown.self="close"
  >
    <component :is="as" :class="panelClass" v-bind="$attrs">
      <header class="ds-dialog__header">
        <div class="ds-dialog__heading">
          <h2 :id="`${id}-modal-title`" class="ds-dialog__title" :data-qa="`${id}-modal-title`">
            {{ title }}
          </h2>
          <p v-if="subtitle" class="ds-dialog__subtitle">{{ subtitle }}</p>
        </div>
        <button
          ref="closeButton"
          type="button"
          class="ds-dialog__close"
          :aria-label="i18n.t('/modal/close')"
          :data-qa="`${id}-modal-button-close`"
          @click="close"
        >
          <svg viewBox="0 0 14 14" width="14" height="14" aria-hidden="true">
            <path d="M2 2l10 10M12 2L2 12" stroke="currentColor" stroke-width="1.8" />
          </svg>
        </button>
      </header>

      <div class="ds-dialog__body" :data-qa="`${id}-modal-content`">
        <slot />
      </div>

      <footer v-if="$slots.footer" class="ds-dialog__footer">
        <slot name="footer" />
      </footer>
    </component>
  </div>
</template>

<script>
// The design system's modal shell: backdrop, panel, title row, scrolling body
// and an optional footer. Everything a dialog has to get right — Escape, the
// backdrop, a focus trap, the scroll lock, returning focus on close — lives
// here once, so a caller only supplies content.
//
// It renders in place rather than in a portal. The panel carries `ds-root`
// itself, so it also looks right when it opens on a legacy page.
//
// `as` lets a form dialog render the panel as a <form>, so the footer's submit
// button and the Enter key still work.

const SIZES = ['sm', 'md', 'lg', 'xl']

// Open instances, bottom first. Stacked dialogs — a confirm on top of a form —
// share one body scroll lock, and only the topmost one answers a key press.
const openDialogs = []

export default {
  name: 'DsDialog',
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: 'sm',
      validator: (value) => SIZES.includes(value),
    },
    as: {
      type: String,
      default: 'div',
    },
  },
  emits: ['close'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      lastFocus: null,
      isOpen: false,
    }
  },
  computed: {
    panelClass() {
      return ['ds-root', 'ds-dialog__panel', `ds-dialog__panel--${this.size}`]
    },
  },
  watch: {
    show: {
      immediate: true,
      handler(open) {
        return open ? this.open() : this.teardown()
      },
    },
  },
  beforeUnmount() {
    this.teardown()
  },
  methods: {
    open() {
      this.isOpen = true
      this.lastFocus = document.activeElement
      openDialogs.push(this)
      if (openDialogs.length === 1) document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.onKeydown)
      // The dialog is only in the DOM after the re-render that set `show`.
      this.$nextTick(() => this.$refs.closeButton?.focus())
    },
    teardown() {
      // Guarded: the watcher also fires on mount with `show` false, and this
      // must not release a scroll lock or a focus another dialog owns.
      if (!this.isOpen) return
      this.isOpen = false

      const index = openDialogs.indexOf(this)
      if (index !== -1) openDialogs.splice(index, 1)

      document.removeEventListener('keydown', this.onKeydown)
      if (!openDialogs.length) document.body.style.removeProperty('overflow')

      if (this.lastFocus) {
        this.lastFocus.focus()
        this.lastFocus = null
      }
    },
    onKeydown(event) {
      if (openDialogs[openDialogs.length - 1] !== this) return
      if (event.key === 'Escape') {
        // Stops one press from closing the dialog underneath as well.
        event.stopPropagation()
        return this.close()
      }
      if (event.key === 'Tab') this.trapFocus(event)
    },
    // Keeps Tab inside the dialog, so focus can never land on the page behind it.
    trapFocus(event) {
      const focusable = this.$refs.backdrop?.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const edge = event.shiftKey ? first : last

      if (
        document.activeElement === edge ||
        !this.$refs.backdrop.contains(document.activeElement)
      ) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      }
    },
    close() {
      this.$emit('close')
    },
  },
}
</script>
